/**
 * REST client for the public CATALOG (guest-first). The public course
 * catalog is SERVER-authoritative (no sync): exploring and opening a course
 * does NOT require an account. Progress, conversely, is private via PowerSync.
 *
 * Types = camelCase DTOs from `models/course.rs` (utoipa). Maintained by hand
 * (catalog does not go through Drizzle schema).
 */

export interface CatalogEntryDTO {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  coverImage: string | null;
  language: string;
  authorName: string | null;
  lessonCount: number;
  enrolledCount: number;
  publishedAt: string | null;
}

export interface CatalogCourseDTO {
  id: string;
  authorId: string;
  title: string;
  description: string | null;
  slug: string | null;
  category: string | null;
  coverImage: string | null;
  language: string;
  visibility: string;
  version: number;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogBlockDTO {
  id: string;
  lessonId: string;
  courseId: string;
  kind: string;
  position: number;
  weight: number;
  content: unknown;
}

export interface CatalogLessonDTO {
  lesson: {
    id: string;
    unitId: string;
    courseId: string;
    title: string;
    position: number;
    xpReward: number;
  };
  blocks: CatalogBlockDTO[];
}

export interface CatalogUnitDTO {
  unit: { id: string; courseId: string; title: string; position: number };
  lessons: CatalogLessonDTO[];
}

export interface CourseTreeDTO {
  course: CatalogCourseDTO;
  units: CatalogUnitDTO[];
  enrolledCount: number;
}

function joinUrl(base: string, path: string): string {
  return `${base.replace(/\/$/, "")}${path}`;
}

/**
 * Parses a server JSON response into its typed DTO. `Response.json()` is `any`,
 * so this is the single validated trust-boundary parse for the catalog
 * endpoints (our own server contract).
 */
async function readJson<T>(res: Response): Promise<T> {
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- trust-boundary parse of our server's typed catalog DTO
  return (await res.json()) as T;
}

/** Lists public catalog (optionally filtered by query string). */
export async function fetchCatalog(
  baseUrl: string,
  opts: { q?: string; signal?: AbortSignal } = {},
): Promise<CatalogEntryDTO[]> {
  const url = new URL(joinUrl(baseUrl, "/api/catalog"));
  if (opts.q && opts.q.trim() !== "") {
    url.searchParams.set("q", opts.q.trim());
  }
  const res = await fetch(url.toString(), { signal: opts.signal });
  if (!res.ok) {
    throw new Error(`catalog fetch failed: ${res.status}`);
  }
  return readJson<CatalogEntryDTO[]>(res);
}

/** Full tree of a public course (for offline consumption after downloading). */
export async function fetchCatalogCourse(
  baseUrl: string,
  courseId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<CourseTreeDTO> {
  const res = await fetch(joinUrl(baseUrl, `/api/catalog/course/${encodeURIComponent(courseId)}`), {
    signal: opts.signal,
  });
  if (!res.ok) {
    throw new Error(`course fetch failed: ${res.status}`);
  }
  return readJson<CourseTreeDTO>(res);
}
